<script lang="tsx">
import { createVNode, render } from 'vue'
import ConfirmModal from './index.vue'
import type { ConfirmModalOptions, ConfirmModalType } from './type'

let container: HTMLElement | null = null
let isDestroying = false

function destroyModal() {
  if (isDestroying || !container) return
  isDestroying = true

  render(null, container)
  container.remove()
  container = null
  isDestroying = false
}

function createModal(options: ConfirmModalOptions & { type: ConfirmModalType }) {
  if (container) {
    destroyModal()
  }

  container = document.createElement('div')
  document.body.appendChild(container)

  const vNode = createVNode(ConfirmModal, {
    ...options,
    visible: true,
  })

  render(vNode, container)
}

export const confirmModal = {
  success(options: ConfirmModalOptions) {
    createModal({ ...options, type: 'success' })
  },
  error(options: ConfirmModalOptions) {
    createModal({ ...options, type: 'error' })
  },
  info(options: ConfirmModalOptions) {
    createModal({ ...options, type: 'info' })
  },
  warning(options: ConfirmModalOptions) {
    createModal({ ...options, type: 'warning' })
  },

  open(options: ConfirmModalOptions & { type: ConfirmModalType }) {
    createModal(options)
  },
  close() {
    if (container) {
      destroyModal()
    }
  }
}
</script>

<script setup lang="tsx">
import { IconCheckCircleFill, IconClose, IconCloseCircleFill, IconExclamationCircleFill, IconInfoCircleFill } from '@arco-design/web-vue/es/icon'
import type { JSX } from 'vue/jsx-runtime'
import type { ConfirmModalProps } from './type'

const props = defineProps<ConfirmModalProps>()
const show = ref(props.visible)
watch(
  () => props.visible,
  val => {
    show.value = val
  }
)
type Emits = {
  (e:'update:visible', val): void
  (e: 'ok', evt: MouseEvent): void
  (e: 'cancel', evt: MouseEvent): void
}

const emits = defineEmits<Emits>()

function onVisibleChange(val: boolean) {
  show.value = val
  emits('update:visible', val)
}

function onCancel(e: MouseEvent) {
  onVisibleChange(false)
  emits('cancel', e)
}
function onOk(e: MouseEvent) {
  emits('ok', e)
}
const typeToIcon: {
  [key in ConfirmModalType]: JSX.Element
} = {
  'success': <IconCheckCircleFill size={30} style={{ color: 'rgb(var(--success-6))' }}  />,
  'error': <IconCloseCircleFill size={30} style={{ color: 'rgb(var(--danger-6))' }} />,
  'info': <IconInfoCircleFill size={30} style={{ color: 'rgb(var(--primary-6))' }} />,
  'warning': <IconExclamationCircleFill size={30} style={{ color: 'rgb(var(--warning-6))' }} />
}
function render() {
  return <a-modal modal-class="rounded-20px" width={`${props.width || 300}px`} visible={show.value} footer={false} hide-title onClose={() => onVisibleChange(false)}>
    <div>
      <div class="flex justify-end">
        <IconClose class="cursor-pointer" onClick={onCancel}/>
      </div>
      <div class="flex justify-center">
        {typeToIcon[props.type]}
      </div>
      <div class="text-center text-15px font-bold mt-10px">{props.title}</div>
      <div class="text-center mt-10px mb-16px">
        {
          props.renderContent
            ? props.renderContent()
            : props.content
        }
      </div>

      <a-divider margin={0} />
      {props.renderFoots ? (
        <div class="pt-12px w-full box-border">
          {props.renderFoots()}
        </div>
      ) : (
        <div class="flex justify-center gap-24px pt-12px">
          {props.showCancel !== false && <a-button class="px-30px" type={props.revertBtnStyle ? 'primary' : undefined} onClick={onCancel}>{props.cancelText ?? '取消'}</a-button>}
          <a-button class="px-30px" type={props.revertBtnStyle ? undefined : 'primary'} onClick={onOk}>{props.okText ?? '确定'}</a-button>
        </div>
      )}
    </div>
  </a-modal>
}
</script>


<template>
  <JSXInTemplate :jsx="render"></JSXInTemplate>
</template>
